import React, { useMemo, useState } from 'react';

import capitalize from 'lodash/capitalize';
import sortBy from 'lodash/sortBy';

import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';

import { useReviewCuration } from '~/hooks/apis/bedbank/useCuration';

import { toDayJs } from '~/services/TimeService';

import CurationImageCard from './components/CurationImageCard';
import CurationImageDetailDialog from './components/CurationImageDetailDialog';
import CurationScoreBar from './components/CurationScoreBar';
import { CLASSIFICATION_COLOR, CLASSIFICATION_LABEL, HERO_IMAGE_COUNT, STATUS_COLOR } from './constants';
import { CurationItem, ImageScore } from './types';

interface Props {
  row: CurationItem;
}

function computeAverageImageScores(images: Array<ImageScore>) {
  if (images.length === 0) {
    return null;
  }

  const dimensions = [
    'lighting',
    'spaciousness',
    'materialQuality',
    'cleanliness',
    'designStyle',
    'photoQuality',
  ] as const;
  const averages: Record<string, number | null> = {};

  for (const dim of dimensions) {
    const valid = images.filter((img) => img[dim] != null);
    averages[dim] = valid.length > 0 ? Math.round(valid.reduce((sum, img) => sum + img[dim], 0) / valid.length) : null;
  }

  const allValues = dimensions.map((d) => averages[d]).filter((v): v is number => v != null);
  const overall = allValues.length > 0 ? Math.round(allValues.reduce((a, b) => a + b, 0) / allValues.length) : null;

  return { ...averages, overall };
}

const DIMENSION_LABELS: Record<string, string> = {
  lighting: 'Lighting',
  spaciousness: 'Spaciousness',
  materialQuality: 'Material Quality',
  cleanliness: 'Cleanliness',
  designStyle: 'Design Style',
  photoQuality: 'Photo Quality',
};

export default function CurationRowDetail({ row }: Props) {
  const [selectedImage, setSelectedImage] = useState<ImageScore | null>(null);
  const [setLiveDialogOpen, setSetLiveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);

  const reviewMutation = useReviewCuration();
  const reasons = row.classificationReasons ?? [];

  const metaScores = [
    { label: 'Star Rating', value: row.starScore },
    { label: 'Guest Rating', value: row.guestRatingScore },
    { label: 'Review Volume', value: row.reviewVolumeScore },
    { label: 'Category', value: row.categoryScore },
    { label: 'Facilities', value: row.facilitiesScore },
    { label: 'Image Count', value: row.imageCountScore },
  ];

  const allImages = useMemo(() => row.imageScores ?? [], [row.imageScores]);
  const heroImages = useMemo(() => sortBy(allImages, (img) => -img.heroScore).slice(0, HERO_IMAGE_COUNT), [allImages]);
  const heroIds = useMemo(() => new Set(heroImages.map((img) => img.id)), [heroImages]);
  const allByLuxury = useMemo(() => sortBy(allImages, (img) => -img.luxuryScore), [allImages]);
  const avgImageScores = useMemo(() => computeAverageImageScores(allImages), [allImages]);

  const handleSetLive = () => {
    reviewMutation.mutate(
      { propertyId: row.propertyId, decision: 'approved' },
      { onSettled: () => setSetLiveDialogOpen(false) },
    );
  };

  const handleReject = () => {
    reviewMutation.mutate(
      { propertyId: row.propertyId, decision: 'rejected' },
      { onSettled: () => setRejectDialogOpen(false) },
    );
  };

  return (
    <Box sx={{ p: 3, bgcolor: 'background.default' }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
          <Link href={`/bedbank/properties/${row.propertyId}`} underline="hover" variant="h6">
            {row.propertyName}
          </Link>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip size="small" label={capitalize(row.status)} color={STATUS_COLOR[row.status]} />
            {row.classification ? (
              <Chip
                size="small"
                label={CLASSIFICATION_LABEL[row.classification]}
                color={CLASSIFICATION_COLOR[row.classification]}
              />
            ) : (
              <Chip size="small" label="N/A" color="default" />
            )}
          </Stack>
          <Box sx={{ textAlign: 'center', flexShrink: 0, ml: 'auto' }}>
            <Typography variant="h4" fontWeight="bold" color="primary">
              {row.combinedScore}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Combined
            </Typography>
          </Box>
        </Stack>
        {!row.reviewDecision && (
          <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
            <Button variant="contained" size="small" onClick={() => setSetLiveDialogOpen(true)}>
              Set Live
            </Button>
            <Button variant="contained" size="small" color="error" onClick={() => setRejectDialogOpen(true)}>
              Reject
            </Button>
          </Stack>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8.4 }}>
          {heroImages.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Top Hero Images
              </Typography>
              <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto', pb: 1 }}>
                {heroImages.map((img) => (
                  <CurationImageCard key={img.id} image={img} isHero large onClick={setSelectedImage} />
                ))}
              </Stack>
            </Box>
          )}

          {allByLuxury.length > 0 && (
            <Accordion disableGutters elevation={0} sx={{ mt: 2, bgcolor: 'transparent' }}>
              <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 0, minHeight: 'auto' }}>
                <Typography variant="subtitle2">All Images ({allByLuxury.length})</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0 }}>
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {allByLuxury.map((img) => (
                    <CurationImageCard
                      key={img.id}
                      image={img}
                      isHero={heroIds.has(img.id)}
                      onClick={setSelectedImage}
                    />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )}

          {allImages.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No image scores available
            </Typography>
          )}
        </Grid>

        <Grid size={{ xs: 12, md: 3.6 }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Image Score
              </Typography>
              <CurationScoreBar
                label="Average Image Quality"
                value={avgImageScores?.overall != null ? avgImageScores.overall * 20 : row.imageQualityScore}
              />
            </Box>

            {avgImageScores && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Image Dimensions
                </Typography>
                <Stack spacing={1}>
                  {Object.entries(DIMENSION_LABELS).map(([key, label]) => (
                    <CurationScoreBar
                      key={key}
                      label={label}
                      value={avgImageScores[key] != null ? (avgImageScores[key] as number) * 20 : null}
                    />
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Metadata Score ({row.metadataScore})
              </Typography>
              <Stack spacing={1}>
                {metaScores.map((score) => (
                  <CurationScoreBar key={score.label} label={score.label} value={score.value} />
                ))}
              </Stack>
            </Box>

            {reasons.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Classification Reasons
                </Typography>
                <Stack spacing={0.5}>
                  {reasons.map((reason) => (
                    <Typography key={reason} variant="body2">
                      {reason}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}

            {row.scoredAt && (
              <Typography variant="caption" color="text.secondary">
                Scored: {toDayJs(row.scoredAt).format('DD MMM YYYY HH:mm')} · Review: {row.reviewDecision ?? 'Pending'}
              </Typography>
            )}
          </Stack>
        </Grid>
      </Grid>

      <CurationImageDetailDialog image={selectedImage} onClose={() => setSelectedImage(null)} />

      <Dialog open={setLiveDialogOpen} onClose={() => setSetLiveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Set Live</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will approve <strong>{row.propertyName}</strong> and set it to <strong>content-approved</strong> with
            domain <strong>all</strong>. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSetLiveDialogOpen(false)} disabled={reviewMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSetLive} disabled={reviewMutation.isPending}>
            {reviewMutation.isPending ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Property</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will reject <strong>{row.propertyName}</strong> from curation. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={reviewMutation.isPending}>
            Cancel
          </Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={reviewMutation.isPending}>
            {reviewMutation.isPending ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
